import React from 'react';
import { Trash2, Copy, GripVertical } from 'lucide-react';
import { EmailBlock } from '../types';

interface BlockWrapperProps {
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onUpdate: (updates: Partial<EmailBlock>) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDarkMode: boolean;
  children: React.ReactNode;
}

export const BlockWrapper: React.FC<BlockWrapperProps> = ({
  block,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  isDarkMode,
  onDragOver,
  onDragLeave,
  onDrop,
  children
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
  };

  return (
    <div
      className={`
        relative group
        ${isSelected 
          ? 'ring-2 ring-blue-500 ring-offset-2' 
          : 'hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600'
        }
        transition-all
      `}
      onClick={handleClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Block content */}
      <div className="relative">
        {children}
      </div>

      {/* Toolbar - shown on hover or when selected */}
      {(isSelected || true) && (
        <div className={`
          absolute -top-8 left-0 right-0 flex items-center justify-center gap-1
          opacity-0 group-hover:opacity-100 transition-opacity
          ${isSelected ? 'opacity-100' : ''}
        `}>
          <div className={`
            flex items-center gap-1 px-2 py-1 rounded
            ${isDarkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-300 shadow-lg'}
          `}>
            <button
              className={`
                p-1 rounded hover:bg-opacity-80
                ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}
              `}
              title="Drag handle"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <button
              onClick={handleDuplicate}
              className={`
                p-1 rounded hover:bg-opacity-80
                ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}
              `}
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className={`
                p-1 rounded hover:bg-opacity-80
                ${isDarkMode ? 'text-error-400 hover:bg-gray-700' : 'text-error-600 hover:bg-gray-100'}
              `}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
